import { Converter } from './Converter';

// Submodules
import * as AppFileApiWrapper from '../submodules/app-api/Filesystem/Wrapper';

export abstract class AppFileApiHelper {
    public static readonly ChunkSize: number = 512000; // 500k

    public static isDirectory(path: string): boolean {
        return path.endsWith('/');
    }

    public static stripTrailingSlash(path: string): string {
        return path.replace(/\/+$/, '');
    }

    public static async directoryIsEmpty(path: string): Promise<boolean> {
        const entries: Array<string> = await AppFileApiWrapper.list(path);
        return entries.length === 0;
    }

    public static async getSubDirectories(path: string, trailingSlash: boolean = true): Promise<Array<string>> {
        const entries: Array<string> = await AppFileApiWrapper.list(path);

        const directories: Array<string> = entries.filter(AppFileApiHelper.isDirectory);

        if (trailingSlash === false) {
            return directories.map(AppFileApiHelper.stripTrailingSlash);
        }

        return directories;
    }

    public static async readTextFile(path: string): Promise<string> {
        return (await AppFileApiHelper.readFile(path, '')) as string;
    }

    public static async readBinaryFile(path: string): Promise<ArrayBuffer> {
        return (await AppFileApiHelper.readFile(path, 'base64')) as ArrayBuffer;
    }

    public static async readFile(path: string, converter: 'base64' | ''): Promise<ArrayBuffer | string | null> {
        let fd: string;
        try {
            fd = await AppFileApiWrapper.open(path, 'r', converter);
        } catch (_) {
            // no such file
            return null;
        }

        if (converter === 'base64') {
            let data: Uint8Array = new Uint8Array(0);
            while (true) {
                const chunk: Uint8Array = Converter.base64ToUint8Array(
                    await AppFileApiWrapper.read(fd, AppFileApiHelper.ChunkSize)
                ) as Uint8Array; // FixMe

                if (data.byteLength === 0 && chunk.byteLength < AppFileApiHelper.ChunkSize) {
                    AppFileApiWrapper.close(fd); // close async
                    return chunk.buffer as ArrayBuffer;
                }

                const newData: Uint8Array = new Uint8Array(data.byteLength + chunk.byteLength);
                newData.set(data, 0);
                newData.set(chunk, data.byteLength);

                data = newData;

                if (chunk.byteLength !== AppFileApiHelper.ChunkSize) {
                    AppFileApiWrapper.close(fd); // close async
                    return data.buffer as ArrayBuffer;
                }
            }
        } else {
            let data: string = '';
            while (true) {
                const chunk: string = await AppFileApiWrapper.read(fd, AppFileApiHelper.ChunkSize);
                data += chunk;

                if (chunk.length < AppFileApiHelper.ChunkSize) {
                    AppFileApiWrapper.close(fd); // close async
                    return data;
                }
            }
        }
    }

    public static async writeFile(path: string, data: ArrayBuffer | string): Promise<void> {
        let fd: string | null = null;
        try {
            if (data instanceof ArrayBuffer) {
                fd = await AppFileApiWrapper.open(path, 'w', 'base64');
                for (let i: number = 0, l: number = data.byteLength; i < l; i +=  AppFileApiHelper.ChunkSize) {
                    await AppFileApiWrapper.write(
                        fd,
                        btoa(
                            Converter.Uint8ArrayToString(
                                new Uint8Array(data.slice(i, i + AppFileApiHelper.ChunkSize))
                            )
                        )
                    );
                }
            } else {
                fd = await AppFileApiWrapper.open(path, 'w', '');
                for (let i: number = 0, l: number = data.length; i < l; i +=  AppFileApiHelper.ChunkSize) {
                    await AppFileApiWrapper.write(
                        fd,
                        data.slice(i, i + AppFileApiHelper.ChunkSize)
                    );
                }
            }

            return AppFileApiWrapper.close(fd);
        } catch (error) {
            if (fd != null) {
                try {
                    await AppFileApiWrapper.close(fd);
                } catch (_) {
                    // ignore error
                }
            }

            return Promise.reject(error);
        }
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/AppFileApiHelper.ts